import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// YouTube utility functions
export function isYouTubeUrl(url: string): boolean {
  return url.includes('youtube.com') || url.includes('youtu.be');
}

export function getYouTubeEmbedUrl(url: string): string {
  if (!isYouTubeUrl(url)) return url;
  
  // Handle YouTube watch URLs
  if (url.includes('youtube.com/watch')) {
    const urlObj = new URL(url);
    const videoId = urlObj.searchParams.get('v');
    const timeParam = urlObj.searchParams.get('t');
    const startParam = urlObj.searchParams.get('start');
    
    if (!videoId) return url;
    
    let embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}`;
    
    // Add start time if present
    const startTime = timeParam ? parseYouTubeTimeToSeconds(timeParam) : startParam;
    if (startTime) {
      embedUrl += `?start=${startTime}`;
    }
    
    return embedUrl;
  }
  
  // Handle YouTube short URLs (youtu.be)
  if (url.includes('youtu.be/')) {
    const urlObj = new URL(url);
    const videoId = urlObj.pathname.substring(1);
    const timeParam = urlObj.searchParams.get('t');
    
    if (!videoId) return url;
    
    let embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}`;
    
    if (timeParam) {
      const startTime = parseYouTubeTimeToSeconds(timeParam);
      if (startTime) {
        embedUrl += `?start=${startTime}`;
      }
    }
    
    return embedUrl;
  }
  
  // Handle existing embed URLs
  if (url.includes('/embed/')) {
    return url.replace('youtube.com', 'youtube-nocookie.com');
  }
  
  return url;
}

export function parseYouTubeTimeToSeconds(timeStr: string): number | null {
  if (!timeStr) return null;
  
  // Handle formats like "1h2m3s", "2m3s", "30s", or plain seconds
  const timeRegex = /(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?/;
  const match = timeStr.match(timeRegex);
  
  if (!match) {
    // Try parsing as plain number (seconds)
    const seconds = parseInt(timeStr, 10);
    return isNaN(seconds) ? null : seconds;
  }
  
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);
  
  return hours * 3600 + minutes * 60 + seconds;
}
